import { classesAbove, readDate, type AboxViewProfile, type ReadDate, type ShownStatement, type StatementDescription } from "../../../knowledgeData";
import type { PlacedStatement } from "./regestParts";

/** What heads one text of the regest (Daniel's render_source): its date and its archive reference. */
export interface TextFacts {
  readonly date: ReadDate | null;
  /** The archive reference as the data names it, with the property that gives it, which names it on screen. */
  readonly archive: { readonly text: string; readonly property: string } | null;
}

/** The regest's header (Daniel's render_vita): how many texts and statements, the years its texts are dated, how far reviewed. */
export interface RegestSummary {
  readonly texts: number;
  readonly statements: number;
  /** The first and the last year its texts are dated, or `null` when none is. */
  readonly years: readonly [number, number] | null;
  readonly accepted: number;
  readonly proposed: number;
}

/**
 * Each part's text facts, by segment path, from the profile's `texts`: an individual of its classes, or a class under
 * one, described in the part's statements is the part's text; its date row reads as one date by the profile's
 * `dates`, its archive row as the reference's own name or words. The first such text in a part counts.
 */
export function partFacts(parts: ReadonlyMap<string, readonly ShownStatement<PlacedStatement>[]>, profile: AboxViewProfile, parentsOf: (iri: string) => readonly string[], language: string): Map<string, TextFacts> {
  const facts = new Map<string, TextFacts>();
  const texts = profile.texts;
  if (texts === null) return facts;
  const isOf = (classes: readonly string[]) => (shape: readonly string[]) => [...classes, ...classesAbove(classes, parentsOf)].some((iri) => shape.includes(iri));
  for (const [segmentPath, items] of parts) {
    // The text's date and its archive reference may come from different statements: each is taken where it is found.
    const descriptions: StatementDescription[] = items.flatMap((item) => [...item.layout.said, ...item.layout.evidence]);
    const byIri = new Map(descriptions.map((description) => [description.individual.iri, description]));
    const rowsOfTexts = descriptions.filter((description) => isOf(description.individual.classes)(texts.classes)).flatMap((description) => description.rows);
    const valueOf = (properties: ReadonlySet<string>) => {
      const row = rowsOfTexts.find((each) => properties.has(each.property) && each.values[0]?.kind === "individual");
      const value = row?.values[0];
      return row === undefined || value?.kind !== "individual" ? null : { row, individual: value.individual };
    };
    const dated = valueOf(texts.date);
    const described = dated === null ? undefined : byIri.get(dated.individual.iri);
    const date = described === undefined ? null : readDate(described, profile.dates, isOf(described.individual.classes), language);
    const archived = valueOf(texts.archive);
    const archiveText = archived === null ? null : archived.individual.name ?? archived.individual.words;
    const archive = archived === null || archiveText === null ? null : { text: archiveText, property: archived.row.property };
    if (date !== null || archive !== null) facts.set(segmentPath, { date, archive });
  }
  return facts;
}

/** The header's counts: the texts, the numbered statements and how many are accepted, and the years of the texts' dates. */
export function regestSummary(texts: number, statements: readonly ShownStatement<PlacedStatement>[], facts: ReadonlyMap<string, TextFacts>): RegestSummary {
  const years = [...facts.values()].flatMap((fact) => (fact.date === null ? [] : [fact.date.year])).sort((a, b) => a - b);
  const first = years[0];
  const last = years.at(-1);
  const accepted = statements.filter((item) => item.shown.accepted).length;
  return { texts, statements: statements.length, years: first === undefined || last === undefined ? null : [first, last], accepted, proposed: statements.length - accepted };
}
